import { type ClassValue, clsx } from "clsx";
import { customAlphabet } from "nanoid";
import { useMemo } from "react";
import { useMatches } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import type { Cluster } from "~/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const DEFAULT_REDIRECT = "/";

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(
  id: string,
): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(
    () => matchingRoutes.find((route) => route.id === id),
    [matchingRoutes, id],
  );
  return route?.data as Record<string, unknown>;
}

export function validateEmail(email: unknown): email is string {
  return typeof email === "string" && email.length > 3 && email.includes("@");
}

export const nanoid = customAlphabet(
  "1234567890abcdefghijklmnopqrstuvwxyz",
  16,
);

export interface SlackCredentials {
  slackToken: string;
  slackAnnounceChannel: string;
}

export function getSlackCredentials(
  clusterId: string,
): SlackCredentials | null {
  const slackToken = process.env.SLACK_WRITE_ONLY_TOKEN;
  if (slackToken) {
    const slackAnnounceChannel = ["operator-test"].includes(clusterId)
      ? "deploy-staging"
      : "deploy";
    return { slackToken, slackAnnounceChannel };
  }
  return null;
}

export async function postSlackMessage(creds: SlackCredentials, text: string) {
  const response = await fetch("https://slack.com/api/chat.postMessage", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${creds.slackToken}`,
    },
    body: JSON.stringify({
      channel: creds.slackAnnounceChannel,
      text,
    }),
  });
  if (!response.ok) {
    console.error("Failed to post to slack", await response.text());
  }
}

export const formatGitSha = (sha: string) => sha.substring(0, 7); // Reasonable length for a Git SHA prefix

/** First line of a commit message. */
export const truncateCommitMessage = (message: string): string => {
  const newlineIndex = message.indexOf("\n");
  return newlineIndex !== -1 ? message.substring(0, newlineIndex) : message;
};

/** "1 cluster", "3 clusters" — count plus a naively pluralized noun. */
export const pluralize = (n: number, noun: string) =>
  `${n} ${noun}${n === 1 ? "" : "s"}`;

// Humanize how old a commit is, e.g. "3 hours old" / "5 days old".
// Hours under 2 days, days beyond that.
export function formatCommitAge(date: string): string {
  const hours = Math.max(
    0,
    Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60)),
  );
  if (hours < 48) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} old`;
  }
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} old`;
}

// Compact elapsed time, e.g. "45s" / "2m 30s" / "1h 34m". Seconds only matter
// while the span is short.
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 10 && seconds % 60 !== 0) return `${minutes}m ${seconds % 60}s`;
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatBytes(bytes: number): string {
  if (bytes == null || isNaN(bytes)) return "N/A";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    sizes.length - 1,
  );
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export async function getSessionToken(): Promise<string | null> {
  try {
    const response = await fetch("/api/session-token", {
      credentials: "include",
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.session_token || null;
  } catch {
    return null;
  }
}

// DISPLAY_USERNAME_COOKIE is set by the api and contains the username (email) of the logged in user.
const DISPLAY_USERNAME_COOKIE = "tpuf_display_username";

export function getDisplayUsername(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${DISPLAY_USERNAME_COOKIE}=`));

  if (!cookie) {
    return null;
  }

  const value = cookie.slice(DISPLAY_USERNAME_COOKIE.length + 1);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export type DatadogScopedCluster = Pick<Cluster, "id" | "datadog_tags">;

/** Datadog tag values scoping the given cluster(s), deduplicated per key. */
function getClusterTags(
  clusters: DatadogScopedCluster | DatadogScopedCluster[],
): Map<string, string[]> {
  const tags = new Map<string, string[]>();
  for (const { datadog_tags } of [clusters].flat()) {
    for (const [key, value] of Object.entries(datadog_tags ?? {})) {
      const values = tags.get(key) ?? [];
      if (!values.includes(value)) values.push(value);
      tags.set(key, values);
    }
  }
  return tags;
}

function getClusterScopeQuery(
  clusters: DatadogScopedCluster | DatadogScopedCluster[],
): string | null {
  const tags = getClusterTags(clusters);
  if (tags.size === 0) return null;
  return [...tags]
    .map(([key, values]) =>
      values.length === 1
        ? `${key}:${values[0]}`
        : `${key}:(${values.join(" OR ")})`,
    )
    .join(" ");
}

/**
 * Generate a Datadog metrics dashboard URL for given cluster(s).
 */
export function getDatadogClusterDashboardUrl(
  clusters: DatadogScopedCluster | DatadogScopedCluster[],
): string | null {
  const tags = getClusterTags(clusters);
  if (tags.size === 0) return null;

  const baseUrl =
    "https://us5.datadoghq.com/dashboard/b9b-pm4-3nv/turbopuffer-production";

  const params = new URLSearchParams();
  params.set("fromUser", "true");
  params.set("overlay", "events");
  params.set("overlayQuery", "env:production");
  params.set("refresh_mode", "paused");

  for (const [key, values] of tags) {
    values.forEach((value, index) => {
      params.set(`tpl_var_${key}[${index}]`, value);
    });
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generate a Datadog logs URL for given cluster(s).
 */
export function getDatadogClusterLogsUrl(
  clusters: DatadogScopedCluster | DatadogScopedCluster[],
): string | null {
  const query = getClusterScopeQuery(clusters);
  if (!query) return null;

  return `https://us5.datadoghq.com/logs?query=${encodeURIComponent(query)}`;
}

/**
 * Generate a Datadog logs URL for a single pod, scoped to the cluster when known.
 */
export function getDatadogPodLogsUrl(
  podName: string,
  cluster?: DatadogScopedCluster,
): string {
  const query = [
    cluster ? getClusterScopeQuery(cluster) : null,
    `pod_name:${podName}`,
  ]
    .filter(Boolean)
    .join(" ");
  return `https://us5.datadoghq.com/logs?query=${encodeURIComponent(query)}`;
}

export function getDatadogTidyJobLogsUrl(
  cluster: DatadogScopedCluster,
  operationId: string,
): string | null {
  const scope = getClusterScopeQuery(cluster);
  if (!scope) return null;

  const query = `${scope} pod_name:tidy-${operationId}-*`;
  return `https://us5.datadoghq.com/logs?query=${encodeURIComponent(query)}`;
}

/**
 * Generate a Datadog APM traces URL for given cluster(s).
 */
export function getDatadogClusterTracesUrl(
  clusters: DatadogScopedCluster | DatadogScopedCluster[],
): string | null {
  const scope = getClusterScopeQuery(clusters);
  if (!scope) return null;

  const query = `-@span.kind:server -@span.kind:client -@span.kind:producer -@span.kind:consumer env:production operation_name:http_request service:turbopuffer ${scope}`;

  return `https://us5.datadoghq.com/apm/traces?query=${encodeURIComponent(query)}`;
}

/**
 * Generate a Datadog metrics dashboard URL for a given org.
 */
export function getDatadogOrgDashboardUrl(orgId: string): string {
  const params = new URLSearchParams({
    fromUser: "true",
    overlay: "events",
    "tpl_var_org_id[0]": orgId,
  });
  return `https://us5.datadoghq.com/dashboard/b9b-pm4-3nv/turbopuffer-production?${params.toString()}`;
}

/**
 * Generate a Datadog logs URL for a given org and optional namespace.
 */
export function getDatadogLogsUrl(orgId: string, namespaceId?: string): string {
  const query = namespaceId
    ? `@org_id:${orgId} @namespace:${namespaceId}`
    : `@org_id:${orgId}`;
  return `https://us5.datadoghq.com/logs?query=${encodeURIComponent(query)}`;
}

/**
 * Generate a Datadog APM traces URL for a given org and optional namespace.
 */
export function getDatadogTracesUrl(
  orgId: string,
  namespaceId?: string,
): string {
  const base =
    "-@span.kind:server -@span.kind:client -@span.kind:producer -@span.kind:consumer env:production operation_name:http_request service:turbopuffer";
  const query = namespaceId
    ? `${base} @org_id:${orgId} @namespace:${namespaceId}`
    : `${base} @org_id:${orgId}`;
  return `https://us5.datadoghq.com/apm/traces?query=${encodeURIComponent(query)}`;
}

const WORKOS_ENVIRONMENT_ID = "environment_01HK510GF9W3TQFN20JZGNEDXJ";

/**
 * Generate a WorkOS dashboard URL for a given WorkOS organization id.
 */
export function getWorkOSUrl(workosId: string): string {
  return `https://dashboard.workos.com/${WORKOS_ENVIRONMENT_ID}/organizations/${workosId}`;
}

/**
 * Format a number as a readable string, e.g. 1000 -> 1K
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

// Strip any prefix ending in a colon (e.g. "namespace_id: abc" or "storage: abc"
// from Datadog copy/paste) so a tagged value can be pasted into a search box directly.
export function stripTagPrefix(value: string): string {
  const colonIdx = value.lastIndexOf(":");
  return (colonIdx !== -1 ? value.slice(colonIdx + 1) : value).trim();
}
